// src/utils/emailService.js
// ============================================================
// Enterprise HRMS — Interview Invitation Email Service
// Uses EmailJS (@emailjs/browser) to send real emails from
// the browser without any backend / Cloud Function required.
//
// Setup Instructions (one-time, by project owner):
//  1. Create a free account at https://www.emailjs.com
//  2. Add a Gmail "Email Service" in the EmailJS dashboard
//     (Connect your Gmail via OAuth — takes 30 seconds)
//  3. Create an Email Template and add these variables:
//       {{to_email}}             - candidate email (MUST be the "To Email" field)
//       {{to_name}}              - candidate full name
//       {{company_name}}         - company name
//       {{applied_position}}     - job position
//       {{interview_round}}      - round name
//       {{interview_date}}       - scheduled date
//       {{interview_time}}       - scheduled time
//       {{duration}}             - duration
//       {{date_time_formatted}}  - combined date+time+duration
//       {{interviewer_name}}     - interviewer name
//       {{meeting_mode}}         - Google Meet / In Person
//       {{meeting_link}}         - Google Meet URL
//       {{notes}}                - special instructions
//       {{email_subject}}        - email subject line
//  4. In the template settings, set "To Email" field to: {{to_email}}
//  5. Copy Service ID, Template ID, and Public Key to .env:
//       REACT_APP_EMAILJS_SERVICE_ID=service_xxxxxxx
//       REACT_APP_EMAILJS_TEMPLATE_ID=template_xxxxxxx
//       REACT_APP_EMAILJS_PUBLIC_KEY=xxxxxxxxxxxxxxx
//  6. Restart the dev server after editing .env
// ============================================================

import emailjs from "@emailjs/browser";
import { db } from "../firebase/config";
import { collection, addDoc, serverTimestamp } from "firebase/firestore";

const EMAILJS_SERVICE_ID  = process.env.REACT_APP_EMAILJS_SERVICE_ID  || "";
const EMAILJS_TEMPLATE_ID = process.env.REACT_APP_EMAILJS_TEMPLATE_ID || "";
const EMAILJS_PUBLIC_KEY  = process.env.REACT_APP_EMAILJS_PUBLIC_KEY  || "";

// Detect placeholder values (not yet replaced by real credentials)
const PLACEHOLDER_PREFIXES = ["YOUR_", "your_", "PLACEHOLDER", "xxx", "TODO"];

function isPlaceholder(val) {
  if (!val) return true;
  return PLACEHOLDER_PREFIXES.some((p) => val.startsWith(p));
}

const EMAILJS_CONFIGURED =
  Boolean(EMAILJS_SERVICE_ID) &&
  Boolean(EMAILJS_TEMPLATE_ID) &&
  Boolean(EMAILJS_PUBLIC_KEY) &&
  !isPlaceholder(EMAILJS_SERVICE_ID) &&
  !isPlaceholder(EMAILJS_TEMPLATE_ID) &&
  !isPlaceholder(EMAILJS_PUBLIC_KEY);

/**
 * Sends an interview invitation email to the candidate via EmailJS.
 *
 * @param {object} details - Interview details
 * @param {string} details.candidateEmail  - Recipient email (REQUIRED)
 * @param {string} details.candidateName   - Candidate full name
 * @param {string} details.candidateId     - Candidate Firestore doc ID
 * @param {string} details.jobTitle        - Applied position
 * @param {string} details.round           - Interview round name
 * @param {string} details.scheduledDate   - Date string (YYYY-MM-DD)
 * @param {string} details.scheduledTime   - Time string (HH:MM)
 * @param {string} details.duration        - e.g. "45 mins"
 * @param {string} details.interviewerName - Interviewer name
 * @param {string} details.meetingMode     - "Google Meet" | "In Person" | etc.
 * @param {string} details.meetingLink     - Google Meet URL or venue
 * @param {string} details.notes           - Extra instructions
 *
 * THROWS on any failure — caller must NOT proceed if this throws.
 * Returns { status: "Sent", emailjsStatus: 200, docId: "..." } on success.
 */
export async function sendInterviewInvitationEmail(details) {
  const companyName     = "Samriddhi Anveshana";
  const candidateName   = (details.candidateName   || "Candidate").trim();
  const candidateEmail  = (details.candidateEmail  || "").trim();
  const appliedPosition = (details.jobTitle || details.appliedPosition || "Open Position").trim();
  const round           = (details.round           || "Technical Round 1").trim();
  const scheduledDate   = (details.scheduledDate   || "").trim();
  const scheduledTime   = (details.scheduledTime   || "").trim();
  const duration        = (details.duration        || "45 mins").trim();
  const interviewerName = (details.interviewerName || "Team Lead").trim();
  const meetingMode     = (details.meetingMode     || "Google Meet").trim();
  const meetingLink     = (details.meetingLink     || "").trim();
  const notes           = (details.notes || details.specialInstructions || "").trim();
  const isOffline       = meetingMode === "In Person" || meetingMode === "Offline";

  // ── Validate recipient email ───────────────────────────────────────────
  if (!candidateEmail || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(candidateEmail)) {
    throw new Error(
      `Cannot send email: "${candidateEmail}" is not a valid email address. ` +
      `Please verify the candidate's email in their profile.`
    );
  }

  // ── Validate EmailJS configuration ────────────────────────────────────
  if (!EMAILJS_CONFIGURED) {
    const missing = [];
    if (!EMAILJS_SERVICE_ID  || isPlaceholder(EMAILJS_SERVICE_ID))  missing.push("REACT_APP_EMAILJS_SERVICE_ID");
    if (!EMAILJS_TEMPLATE_ID || isPlaceholder(EMAILJS_TEMPLATE_ID)) missing.push("REACT_APP_EMAILJS_TEMPLATE_ID");
    if (!EMAILJS_PUBLIC_KEY  || isPlaceholder(EMAILJS_PUBLIC_KEY))  missing.push("REACT_APP_EMAILJS_PUBLIC_KEY");

    throw new Error(
      `EmailJS is not configured. Add the following to your .env file and restart the server:\n` +
      missing.map((k) => `  ${k}=<your_value>`).join("\n") +
      `\n\nGet these from: https://www.emailjs.com → Dashboard → Email Services & Templates`
    );
  }

  // Format date for human reading, e.g. "Thursday, 14 August 2026"
  let formattedDate = scheduledDate;
  try {
    const d = new Date(scheduledDate + "T00:00:00");
    formattedDate = d.toLocaleDateString("en-IN", {
      weekday: "long", year: "numeric", month: "long", day: "numeric",
    });
  } catch { /* keep raw string */ }

  // Format time for human reading, e.g. "2:00 PM IST" (bulletproof 12-hour format)
  let formattedTime = scheduledTime || "02:00 PM";
  if (scheduledTime) {
    try {
      const parts = String(scheduledTime).trim().split(":");
      if (parts.length >= 2) {
        const hours = parseInt(parts[0], 10);
        const minutes = parseInt(parts[1], 10);
        if (!isNaN(hours) && !isNaN(minutes)) {
          const period = hours >= 12 ? "PM" : "AM";
          const displayHours = hours % 12 || 12;
          const displayMinutes = minutes < 10 ? `0${minutes}` : minutes;
          formattedTime = `${displayHours}:${displayMinutes} ${period} IST`;
        }
      }
    } catch {
      formattedTime = scheduledTime;
    }
  }

  let cleanCandidateName = (details.candidateName || "").trim();
  if (!cleanCandidateName) {
    cleanCandidateName = candidateEmail.split("@")[0] || "Candidate";
  }

  const emailSubject = `Interview Invitation – ${appliedPosition} | ${companyName}`;
  const dateTimeFormatted = `${formattedDate} at ${formattedTime} (${duration})`;
  const dateTimeSimple = `${formattedDate} ${formattedTime}`;

  const templateParams = {
    // Recipient & Name variations
    to_email:            candidateEmail,
    candidate_email:     candidateEmail,
    candidateEmail:      candidateEmail,
    email:               candidateEmail,

    to_name:             cleanCandidateName,
    candidate_name:      cleanCandidateName,
    candidateName:       cleanCandidateName,
    name:                cleanCandidateName,
    candidate:           cleanCandidateName,
    full_name:           cleanCandidateName,
    fullName:            cleanCandidateName,

    // Company
    company_name:        companyName,
    companyName:         companyName,
    company:             companyName,

    // Position / Role
    applied_position:    appliedPosition,
    appliedPosition:     appliedPosition,
    position:            appliedPosition,
    job_title:           appliedPosition,
    jobTitle:            appliedPosition,
    role:                appliedPosition,

    // Round
    interview_round:     round,
    interviewRound:      round,
    round:               round,

    // Date variations
    interview_date:      formattedDate,
    interviewDate:       formattedDate,
    scheduled_date:      scheduledDate,
    scheduledDate:       scheduledDate,
    date:                formattedDate,
    Date:                formattedDate,

    // Time variations (All possible casing / variable names)
    interview_time:      formattedTime,
    interviewTime:       formattedTime,
    scheduled_time:      formattedTime,
    scheduledTime:       formattedTime,
    schedule_time:       formattedTime,
    scheduleTime:        formattedTime,
    time:                formattedTime,
    Time:                formattedTime,
    timing:              formattedTime,
    Timing:              formattedTime,
    time_slot:           formattedTime,
    timeSlot:            formattedTime,
    slot:                formattedTime,
    start_time:          formattedTime,
    startTime:           formattedTime,
    event_time:          formattedTime,
    eventTime:           formattedTime,
    raw_time:            scheduledTime,

    duration:            duration,

    // Combined Date & Time variations
    date_time:           dateTimeFormatted,
    dateTime:            dateTimeFormatted,
    date_and_time:       dateTimeFormatted,
    dateAndTime:         dateTimeFormatted,
    datetime:            dateTimeFormatted,
    date_time_formatted: dateTimeFormatted,
    dateTimeFormatted:   dateTimeFormatted,
    interview_date_time: dateTimeFormatted,
    interviewDateTime:   dateTimeFormatted,
    scheduled_date_time: dateTimeFormatted,
    scheduledDateTime:   dateTimeFormatted,
    date_time_simple:    dateTimeSimple,

    // Interviewer
    interviewer_name:    interviewerName,
    interviewerName:     interviewerName,
    interviewer:         interviewerName,
    lead_name:           interviewerName,
    leadName:            interviewerName,

    // Mode
    meeting_mode:        meetingMode,
    meetingMode:         meetingMode,
    mode:                meetingMode,

    // Meeting Link / Location variations
    meeting_link:        isOffline ? "In-Person Interview – location details below" : meetingLink,
    meetingLink:         isOffline ? "In-Person Interview – location details below" : meetingLink,
    meeting_url:         isOffline ? "In-Person Interview – location details below" : meetingLink,
    meetingUrl:          isOffline ? "In-Person Interview – location details below" : meetingLink,
    meet_link:           meetingLink,
    meetLink:            meetingLink,
    google_meet_link:    meetingLink,
    googleMeetLink:      meetingLink,
    url:                 meetingLink,
    link:                isOffline ? "In-Person Interview – location details below" : meetingLink,
    location:            isOffline ? meetingLink : "Online (Google Meet)",
    meeting_location:    isOffline ? meetingLink : meetingLink,
    meetingLocation:     isOffline ? meetingLink : meetingLink,

    // Notes
    notes:               notes || "No special instructions.",
    special_instructions: notes || "No special instructions.",
    specialInstructions: notes || "No special instructions.",

    // Subject & Status
    email_subject:       emailSubject,
    emailSubject:        emailSubject,
    subject:             emailSubject,
    is_online:           isOffline ? "no" : "yes",
  };

  console.log("[sendInterviewInvitationEmail] Sending to:", candidateEmail, "via EmailJS...");

  // ── Call EmailJS ────────────────────────────────────────────────────────
  let emailjsResponse;
  try {
    emailjsResponse = await emailjs.send(
      EMAILJS_SERVICE_ID,
      EMAILJS_TEMPLATE_ID,
      templateParams,
      EMAILJS_PUBLIC_KEY
    );
  } catch (emailjsErr) {
    // EmailJS can throw a string, an object with .text, or a standard Error
    const reason =
      emailjsErr?.text ||
      emailjsErr?.message ||
      (typeof emailjsErr === "string" ? emailjsErr : JSON.stringify(emailjsErr));

    console.error("[sendInterviewInvitationEmail] EmailJS send failed:", emailjsErr);

    // Give the user an actionable error message
    let userFriendlyError = `Email delivery failed: ${reason}`;
    if (reason.includes("service_id") || reason.includes("service not found")) {
      userFriendlyError = `EmailJS Service ID is invalid or not found. Check REACT_APP_EMAILJS_SERVICE_ID in .env.`;
    } else if (reason.includes("template_id") || reason.includes("template not found")) {
      userFriendlyError = `EmailJS Template ID is invalid. Check REACT_APP_EMAILJS_TEMPLATE_ID in .env.`;
    } else if (reason.includes("public key") || reason.includes("Invalid user")) {
      userFriendlyError = `EmailJS Public Key is invalid. Check REACT_APP_EMAILJS_PUBLIC_KEY in .env.`;
    } else if (reason.includes("Gmail") || reason.includes("google") || reason.includes("SMTP")) {
      userFriendlyError = `Gmail / SMTP authentication failed. Re-connect your Gmail account in EmailJS dashboard.`;
    } else if (reason.includes("quota") || reason.includes("limit")) {
      userFriendlyError = `EmailJS monthly quota exceeded (free plan: 200/month). Upgrade at emailjs.com.`;
    }

    throw new Error(userFriendlyError);
  }

  // ── Confirm success (EmailJS returns status 200 on success) ────────────
  if (emailjsResponse.status !== 200) {
    throw new Error(
      `Email delivery failed with status ${emailjsResponse.status}: ${emailjsResponse.text}. Interview was NOT scheduled.`
    );
  }

  console.log("[sendInterviewInvitationEmail] ✅ Email delivered to:", candidateEmail, "| Status:", emailjsResponse.status);

  // ── Audit log (non-critical — never blocks success) ────────────────────
  let auditDocId = "";
  try {
    const auditRef = await addDoc(collection(db, "emailLog"), {
      to:            candidateEmail,
      candidateName: cleanCandidateName,
      appliedPosition,
      round,
      scheduledDate,
      scheduledTime,
      formattedDate,
      formattedTime,
      interviewerName,
      meetingMode,
      meetingLink:   isOffline ? "" : meetingLink,
      subject:       emailSubject,
      emailSent:     true,
      emailjsStatus: emailjsResponse.status,
      candidateId:   details.candidateId || "",
      interviewId:   details.interviewId || "",
      sentAt:        serverTimestamp(),
    });
    auditDocId = auditRef.id;
  } catch (auditErr) {
    console.warn("[sendInterviewInvitationEmail] Audit log failed (non-critical):", auditErr);
  }

  return {
    status:        "Sent",
    emailjsStatus: emailjsResponse.status,
    docId:         auditDocId,
    recipient:     candidateEmail,
  };
}
